/**
 * The browser half of the session timeline: what the user did, batched and shipped to the server as
 * `client_action` wide events, plus the in-memory breadcrumb trail attached to a crash report.
 *
 * WHAT IS COLLECTED, and why it stops there. Two action types only:
 *   - `nav`  — a resolved route change, labelled by the route path.
 *   - `click` — a click on an interactive element, labelled by its ACCESSIBLE NAME (the visible text,
 *     aria-label or title), truncated.
 *
 * Never keystrokes, never the value of an input, never the contents of a form, never DOM state. This
 * is a debugging aid for "what were they doing when it broke", not session replay, and the donation
 * flow this app exists to serve carries names, emails and payment details across its forms. Labels
 * come from elements the user chose to press, not from anything they typed.
 *
 * That last rule is necessary but not sufficient: a RENDERED label can carry personal data even
 * though the user never typed it here — a donations table whose rows are links, a "Resend receipt to
 * jane@example.com" button. So every label is run through `sanitizeLabel` before it is recorded.
 *
 * `sanitizeLabel` catches the patterned identifiers (emails, long digit runs, IBANs). It CANNOT catch a
 * person's name — `Jane Doe` is indistinguishable from `Donate Now` by shape, and any heuristic broad
 * enough to catch it would redact half the buttons in the app. So a name rendered into a control's text
 * would otherwise reach durable analytics storage. Mark any subtree that renders donor, contact or
 * beneficiary data with `data-no-telemetry`: clicks inside it are still recorded (knowing the user
 * pressed something there is the point) but labelled with a structural hint instead of their text.
 *
 * Client-only module. Every entry point is a no-op on the server and swallows its own failures — a
 * telemetry bug must never break a navigation or a click.
 */
import { recordClientActionsFn } from './data';
import { telemetryHeaders } from './telemetry-headers';
// Type-only, so nothing from the monolith reaches the client bundle. Shared rather than redeclared: a
// field added on one side and not the other would silently never arrive.
import type { ClientAction, ClientActionType, ClientErrorBreadcrumb } from '@worldcoo/monolith/wide-events';

/**
 * Marks a subtree whose rendered text may contain personal data (donor names, contact details), so
 * clicks inside it are labelled structurally rather than by their text. Put it on the container — the
 * table, the card, the list — not on every control.
 */
export const TELEMETRY_OPT_OUT_ATTRIBUTE = 'data-no-telemetry';
const TELEMETRY_OPT_OUT_SELECTOR = `[${TELEMETRY_OPT_OUT_ATTRIBUTE}]`;

const MAX_LABEL_LEN = 120;
const BATCH_AT_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
/** How many actions the crash report carries. Enough for context, small enough to stay a side note. */
const BREADCRUMB_LIMIT = 20;

let pending: ClientAction[] = [];
let breadcrumbs: ClientAction[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;

/** The recent action trail, oldest first, in the shape `logErrorFn` expects. */
export function recentBreadcrumbs(): ClientErrorBreadcrumb[] {
	return breadcrumbs.map((action) => ({ t: action.t, type: action.type, message: `${action.label} (${action.path})` }));
}

/**
 * A `fetch` that survives the page going away. The browser cancels in-flight requests on unload, and
 * the batch pending at that moment is the LAST thing the user did — the most valuable one in the
 * buffer and, before a crash or a rage-quit, the whole reason to keep a timeline. `keepalive` lets
 * the request outlive the document. (`sendBeacon` would do the same but can't carry the server
 * function's serialized payload and headers, so it isn't an option here.)
 */
const keepaliveFetch = (...args: Parameters<typeof fetch>): Promise<Response> =>
	fetch(args[0], { ...args[1], keepalive: true });

function flush(options?: { unloading?: boolean }): void {
	if (flushTimer) {
		clearTimeout(flushTimer);
		flushTimer = undefined;
	}
	if (pending.length === 0) return;
	const batch = pending;
	pending = [];
	recordClientActionsFn({
		data: { actions: batch },
		// Only on the unload path: `keepalive` bodies share a small per-page budget (64KB across all
		// in-flight keepalive requests), so spending it on routine flushes would starve the one that
		// actually needs it.
		//
		// A per-call `fetch` REPLACES the app-wide one rather than wrapping it (TanStack resolves
		// `opts.fetch ?? startOptions.serverFns.fetch`), and the app-wide one is what attaches the
		// telemetry headers. So they have to be passed explicitly here, or the unload batch — the most
		// valuable one in the buffer — would be the only batch landing with no trace or session id.
		...(options?.unloading ? { fetch: keepaliveFetch, headers: telemetryHeaders() } : {}),
	}).catch(() => {
		// Dropped rather than retried: a retry queue for best-effort telemetry costs more (memory, a
		// thundering retry on recovery) than the events are worth.
	});
}

function scheduleFlush(): void {
	if (flushTimer) return;
	flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

/**
 * Strip personal data that a rendered label can carry even though nobody typed it into this page.
 * Emails, long digit runs (card, phone, document and account numbers) and IBANs become placeholders;
 * the surrounding words, which are what actually identify the control, survive.
 *
 * Deliberately coarse. Over-redacting a label costs a little debugging clarity; under-redacting one
 * writes a donor's email into durable analytics storage.
 */
export function sanitizeLabel(label: string): string {
	return (
		label
			.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
			.replace(/\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/g, '[iban]')
			// 6+ digits, tolerating the spaces and dashes these numbers are usually formatted with.
			.replace(/\b[\d][\d\s-]{4,}\d\b/g, '[number]')
			.replace(/\s+/g, ' ')
			.trim()
	);
}

function record(type: ClientActionType, label: string, path: string): void {
	if (typeof window === 'undefined') return;
	const action: ClientAction = { t: Date.now(), type, label: sanitizeLabel(label).slice(0, MAX_LABEL_LEN), path };
	breadcrumbs.push(action);
	if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs = breadcrumbs.slice(-BREADCRUMB_LIMIT);
	pending.push(action);
	if (pending.length >= BATCH_AT_SIZE) flush();
	else scheduleFlush();
}

/**
 * The accessible name of the interactive element a click landed on, or null when the click didn't hit
 * one. Walks up from the target because a click usually lands on the text or icon INSIDE a button.
 */
function interactiveLabel(target: EventTarget | null): string | null {
	if (!(target instanceof Element)) return null;
	// `closest` does the walking: a click usually lands on the text or icon INSIDE a button.
	const interactive = target.closest('a, button, [role="button"], [role="link"], summary');
	if (!interactive) return null;
	// A structural hint for anything inside an opted-out subtree. `sanitizeLabel` cannot recognise a
	// person's name, so a region that renders one has to say so; the click is still recorded, just
	// without its text. See the module doc.
	if (interactive.closest(TELEMETRY_OPT_OUT_SELECTOR)) return `<${interactive.tagName.toLowerCase()}>`;
	const label =
		interactive.getAttribute('aria-label') ??
		interactive.getAttribute('title') ??
		// textContent, not innerHTML and never `value`: the visible label of a control the user pressed.
		interactive.textContent?.trim();
	// An icon-only control with no accessible name still tells us something was pressed; fall back to a
	// structural hint rather than dropping the action or inventing a label.
	return label || `<${interactive.tagName.toLowerCase()}>`;
}

let installed = false;

/**
 * Start recording the session timeline. Idempotent, browser-only. `subscribeToNavigations` is the
 * router's history subscription, passed in so this module doesn't have to know about the router.
 */
export function installSessionActionTracking(
	subscribeToNavigations: (onNavigate: (path: string) => void) => void,
): void {
	if (installed || typeof window === 'undefined') return;
	installed = true;

	record('nav', 'initial-load', window.location.pathname);
	subscribeToNavigations((path) => {
		record('nav', path, path);
	});

	// Capture phase, so an action is recorded even when a handler stops propagation — the clicks worth
	// seeing before a crash are frequently the ones whose handler then threw.
	window.addEventListener(
		'click',
		(event) => {
			try {
				const label = interactiveLabel(event.target);
				if (label) record('click', label, window.location.pathname);
			} catch {
				// Never let telemetry interfere with a click.
			}
		},
		{ capture: true, passive: true },
	);

	// A tab being hidden or closed is the last chance to ship what's buffered; `visibilitychange` is
	// the reliable one, `pagehide` covers the bfcache path that never fires it.
	window.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') flush({ unloading: true });
	});
	window.addEventListener('pagehide', () => {
		flush({ unloading: true });
	});
}
